import type { createFloorMaps, createSteelMaps, SurfaceCanvas } from "./surfaceMaps";
import { yieldSceneWork } from "./runtimeBudget";

export const BISPLATE_ASSET_BASE = "/assets/textures/home-bisplate/v1";
export type SurfaceImage = SurfaceCanvas | ImageBitmap | HTMLImageElement;
export type BakedEnvironment = { pixels: Uint16Array; width: number; height: number };

export type SurfaceMaps = {
  branding?: SurfaceImage;
  steel: Record<keyof ReturnType<typeof createSteelMaps>, SurfaceImage | null>;
  floor: Record<keyof ReturnType<typeof createFloorMaps>, SurfaceImage | null>;
};

let cachedMaps: Promise<SurfaceMaps> | undefined;
const cachedEnvironments = new Map<string, Promise<BakedEnvironment>>();

export function hasCompleteSurfaceMaps(maps: SurfaceMaps | null | undefined): maps is SurfaceMaps {
  return Boolean(maps &&
    ["albedo", "roughness", "metalness", "normal"].every(key => {
      const image = maps.steel?.[key as keyof SurfaceMaps["steel"]];
      return image && image.width === 768 && image.height === 768;
    }) &&
    ["color", "roughness", "bump", "alpha"].every(key => {
      const image = maps.floor?.[key as keyof SurfaceMaps["floor"]];
      return image && image.width === 512 && image.height === 512;
    }));
}

async function loadImage(name: string) {
  if (typeof Image === "undefined") {
    const response = await fetch(`${BISPLATE_ASSET_BASE}/${name}.webp`);
    if (!response.ok) throw new Error(`Steel texture unavailable: ${name}`);
    return createImageBitmap(await response.blob(), { imageOrientation: "flipY", premultiplyAlpha: "none" });
  }
  const image = new Image();
  image.decoding = "async";
  image.fetchPriority = "low";
  image.src = `${BISPLATE_ASSET_BASE}/${name}.webp`;
  await image.decode();
  return image;
}

export const loadBrandingMap = () => loadImage("branding");

/** Exact original textures, losslessly baked once rather than recomputed per visitor. */
export function loadSurfaceMaps(): Promise<SurfaceMaps> {
  cachedMaps ??= Promise.all([
    "steel-albedo", "steel-roughness", "steel-metalness", "steel-normal",
    "floor-color", "floor-roughness", "floor-bump", "floor-alpha", "branding",
  ].map(loadImage)).then(([albedo, roughness, metalness, normal, color, floorRoughness, bump, alpha, branding]) => {
    const maps = { steel: { albedo, roughness, metalness, normal, bump: null }, floor: { color, roughness: floorRoughness, bump, alpha }, branding };
    if (!hasCompleteSurfaceMaps(maps)) throw new Error("Full-resolution steel maps are unavailable");
    return maps;
  }).catch((error) => {
    cachedMaps = undefined;
    throw error;
  });
  return cachedMaps;
}

/** Exact half-float HDR values and the original prefiltered lighting atlas. */
export function loadBakedEnvironment(base = BISPLATE_ASSET_BASE): Promise<BakedEnvironment> {
  if (cachedEnvironments.has(base)) return cachedEnvironments.get(base)!;
  const result = (async () => {
    const compressed = typeof DecompressionStream !== "undefined";
    const response = await fetch(`${base}/${compressed ? "environment-planes.bin.gz" : "environment.bin"}`);
    if (!response.ok) throw new Error("Steel lighting could not be loaded");
    // Some hosts already decode .gz via Content-Encoding; never decompress twice.
    const decodedByHost = /\bgzip\b/i.test(response.headers.get("Content-Encoding") ?? "");
    const stream = compressed && !decodedByHost ? response.body?.pipeThrough(new DecompressionStream("gzip")) : response.body;
    if (!stream) throw new Error("Missing lighting data");
    const buffer = await new Response(stream).arrayBuffer();
    if (buffer.byteLength < 8) throw new Error("Invalid lighting data");
    const header = new DataView(buffer), width = header.getUint32(0, true), height = header.getUint32(4, true);
    if (!width || !height || width * height * 8 + 8 !== buffer.byteLength) throw new Error("Invalid lighting dimensions");
    let pixels = new Uint16Array(buffer, 8);
    if (compressed) {
      // Byte-plane packing improves lossless HDR compression; no values discarded.
      const bytes = new Uint8Array(buffer, 8), count = width * height * 4;
      pixels = new Uint16Array(count);
      for (let start = 0; start < count; start += 65_536) {
        const end = Math.min(count, start + 65_536);
        for (let index = start; index < end; index++) pixels[index] = bytes[index] | (bytes[count + index] << 8);
        await yieldSceneWork();
      }
    }
    return { width, height, pixels };
  })().catch(error => { cachedEnvironments.delete(base); throw error; });
  cachedEnvironments.set(base, result);
  return result;
}
